import {
  AdGroup,
  IDigitalizationDocumentSummaryPageOptionsDto,
  IDigitalizationProjectsPageOptionsDto,
  IDigitalizationUploadSummaryPageOptionsDto,
  IDocumentsUploadsSummaryPageOptionsDto,
  IFilesQueryRequest,
  ISupplier,
  ISuppliersSummaryPageOptionsDto,
  IWorkspaceDocumentsListPageOptionsDto,
  IWorkspaceReadOnlyDocumentsListPageOptionsDto,
  ProjectType,
  QualityCheckStatus,
  UserGroup,
  WebTour,
} from 'types';
import { transformPageOptionsToQueryString } from './transformPageOptionsToQueryString';

export default {
  general: {
    userRoles: 'roles',
    userData: '/user',
    env: '/env',
  },

  redirections: {
    prefix: 'redirections',
    getDaisyProjectRoomUrl: function (projectTitle: string) {
      return `${this.prefix}/daisy-project-room/${projectTitle}`;
    },
  },

  projects: {
    getAllProjects: 'projects',
    addProject: 'projects',
    getProjectById: (projectId: string) => `projects/${projectId}`,
    getProjectNameById: (projectId: string) => `projects/${projectId}/name`,
    getProjectLocation: (projectId: string) => `projects/${projectId}/location`,
    updateProject: (projectId: string) => `projects/${projectId}`,
    // TODO: Refactor endpoint
    getProjectSuppliersById: (
      projectId: string,
      fields?: (keyof ISupplier)[],
      includeDeleted?: boolean,
    ) => {
      let url = `projects/suppliers/${projectId}`;
      if (fields?.length) {
        const commaSeparatedFields = fields.join(',');
        url = `${url}?fields=${commaSeparatedFields}`;
      }
      if (includeDeleted) {
        if (url.includes('?')) {
          url = `${url}&includeDeleted=true`;
        } else {
          url = `${url}?includeDeleted=true`;
        }
      }

      return url;
    },
    addSupplierToProject: (projectId: string) =>
      `projects/suppliers/add-supplier/${projectId}`,
    addCoworkerToProject: (projectId: string, coworkerOid: string) =>
      `projects/coworkers/add-coworker/${projectId}/${coworkerOid}`,
    getCoworkersForProjectForSupplier: (
      projectId: string,
      supplierId: string,
    ) =>
      `projects/coworkers-in-project/${projectId}/for-supplier/${supplierId}`,
    addDocuManagerToProject: (projectId: string, docuManagerOid: string) =>
      `projects/docu-managers/add/${projectId}/${docuManagerOid}`,
    getDocuManagersForProject: (projectId: string) =>
      `projects/docu-managers/${projectId}`,
    removeDocuManagerFromProject: (projectId: string, docuManagerOid: string) =>
      `projects/docu-managers/${projectId}/${docuManagerOid}`,
    getInspectorsForProject: (projectId: string) =>
      `projects/inspectors/${projectId}`,
    addInspectorToProject: (projectId: string, inspectorOid: string) =>
      `projects/inspectors/${projectId}/${inspectorOid}`,
    removeInspectorFromProject: (projectId: string, inspectorOid: string) =>
      `projects/inspectors/${projectId}/${inspectorOid}`,
    setInspectionDueTime: (projectId: string) =>
      `projects/inspection-due-time/${projectId}`,
    getInspectionDueTime: (projectId: string) =>
      `projects/inspection-due-time/${projectId}`,
    removeCoworkerFromProject: (projectId: string, coworkerOid: string) =>
      `projects/coworker/${projectId}/${coworkerOid}`,
    getProjectIdByExternalId: (externalProjectId: string) =>
      `projects/project-id-by-external-id/${externalProjectId}`,
  },

  suppliers: {
    getSuppliersSummary: (
      pageOptions: ISuppliersSummaryPageOptionsDto,
    ): string => {
      return transformPageOptionsToQueryString('suppliers', pageOptions);
    },
    searchSuppliers: (searchData: { name: string; SAPNumber: string }) =>
      `suppliers/query?name=${searchData.name}&SAPNumber=${searchData.SAPNumber}`,
    getSupplierById: (supplierId: string) => `suppliers/id/${supplierId}`,
    getSupplierNameById: (supplierId: string) => `suppliers/name/${supplierId}`,
    getCoworkersForSupplier: (supplierId: string) =>
      `suppliers/${supplierId}/coworkers`,
    addCoworkerToSupplier: (supplierId: string, coworkerOid: string) =>
      `suppliers/${supplierId}/coworkers/${coworkerOid}`,
  },

  projectsSuppliers: {
    prefix: 'projects-suppliers',
    getProjectsSuppliersSummary: function (projectId: string) {
      return `${this.prefix}/summary/${projectId}`;
    },
    getContentTypesListForSupplierInProject: function (
      projectId: string,
      supplierId: string,
    ) {
      return `${this.prefix}/upload-count-by-content-type-names/${projectId}/${supplierId}`;
    },
    removeSupplierFromProject: function (
      projectId: string,
      supplierId: string,
    ) {
      return `${this.prefix}/projects-supplier/${projectId}/${supplierId}`;
    },
  },

  contentTypeMappings: {
    addAssignmentToContentType: (contentTypeId: string) =>
      `content-types/assignments/${contentTypeId}`,
    getAvailableContentTypes: 'content-types/available',
    getContentTypesAssignmentsForProject: (projectId: string) =>
      `content-types/assignments/${projectId}`,
    addFieldsMapping: (
      contentTypeId: string,
      projectId: string,
      supplierId: string,
    ) =>
      `content-types/fields-mapping/${contentTypeId}/${projectId}/${supplierId}`,
    getFieldsMapping: (
      contentTypeId: string,
      projectId: string,
      supplierId: string,
    ) =>
      `content-types/fields-mapping/${contentTypeId}/${projectId}/${supplierId}`,
    getFieldsMappingConfiguration: (
      contentTypeId: string,
      projectId: string,
      supplierId: string,
    ) =>
      `content-types/fields-mapping-configuration/${contentTypeId}/${projectId}/${supplierId}`,
    getCheckBotMapping: (contentTypeId: string) =>
      `content-types/checkbot-mapping/${contentTypeId}`,
    getContentTypeNamesForProject: (projectId: string) =>
      `content-types/names/${projectId}`,
    getDigitalizationProjectContentTypesSummary: (
      digitalizationProjectId: string,
    ) =>
      `content-types/digitalization-project/assignments/${digitalizationProjectId}`,
    getDigitalizationContentTypeAndCheckBotMapping: (
      contentTypeId: string,
      digitalizationProjectId: string,
    ) =>
      `content-types/digitalization-project/assignments/fields-mapping/${contentTypeId}/${digitalizationProjectId}`,
    getDigitalizationProjectContentTypeConfig: (
      contentTypeId: string,
      digitalizationProjectId: string,
    ) =>
      `content-types/digitalization-project/assignments/fields-mapping-configuration/${contentTypeId}/${digitalizationProjectId}`,
    createDigitalizationProjectContentTypeMapping:
      'content-types/digitalization-project/assignments/fields-mapping',
    addDigitalizationProjectContentTypeAssignment:
      'content-types/digitalization-project/assignments',
    getContentTypeById: (
      contentTypeId: string,
      includeUniversalMappingFields = false,
    ) =>
      `content-types/schemas/id/${contentTypeId}?includeUniversalMappingFields=${includeUniversalMappingFields}`,
    getPossibleValuesForContentTypesTaxonomyFields: (contentTypeId: string) =>
      `content-types/taxonomy-values/id/${contentTypeId}`,
  },

  sharePointIntegration: {
    /** @deprecated */
    getAllUsersFromDigitalizationProjectSpGroup: (groupName: UserGroup) =>
      `share-point-integration/group-members/digitalization-projects/${groupName}`,
    /** @deprecated */
    getAllUsersFromWorkspacesSpGroup: (groupName: UserGroup) =>
      `share-point-integration/group-members/workspaces/${groupName}`,
    /** @deprecated */
    getAllUsersFromSupplierProjectSpGroup: (groupName: UserGroup) =>
      `share-point-integration/group-members/supplier-projects/${groupName}`,
  },

  adIntegration: {
    getAllUsersFromDigitalizationProjectAdGroup: (groupName: AdGroup) =>
      `ad-integration/group-members/digitalization-projects/${groupName}`,
    getAllUsersFromWorkspacesAdGroup: (groupName: AdGroup) =>
      `ad-integration/group-members/workspaces/${groupName}`,
    getAllUsersFromSupplierProjectAdGroup: (groupName: AdGroup) =>
      `ad-integration/group-members/supplier-projects/${groupName}`,
  },

  documentsUploads: {
    getUploadsInUploadViewFormat: (projectId: string) =>
      `documents-uploads/upload-view-format/${projectId}`,
    getFilesForProject: (
      projectId: string,
      page: number,
      perPage: number,
      sorting: string,
      sortDirection: 1 | -1,
      query?: Partial<IFilesQueryRequest>,
    ): string => {
      let url = `documents-uploads/files/${projectId}/${page}/${perPage}/${sorting}/${sortDirection}`;

      // TODO: extract function as helper
      for (const field in query) {
        const value = query[field as keyof IFilesQueryRequest];

        if (value) {
          if (!url.includes('?')) {
            url = `${url}?${field}=${value}`;
          } else {
            url = `${url}&${field}=${value}`;
          }
        }
      }

      return url;
    },
    getFile: (documentOid: string, includeCheckBotConfig?: boolean) =>
      `documents-uploads/file/${documentOid}${includeCheckBotConfig ? '?includeCheckBotConfig=true' : ''}`,
    assignInspectorStatus: (documentOid: string) =>
      `documents-uploads/inspector-status/${documentOid}`,
    assignInspectorStatusToMultipleFiles:
      'documents-uploads/inspector-statuses',
    getUploadsCountForSupplierInProject: (
      projectId: string,
      supplierId: string,
    ) =>
      `documents-uploads/project/${projectId}/supplier/${supplierId}/uploads-count`,
    validateReferenceNumber: (projectId: string): string =>
      `documents-uploads/reference-number/validate/${projectId}`,
    changeInspectionDueDates: 'documents-uploads/inspection-due-dates/update',
    getPaginatedDocumentsUploadsSummaryForProject: (
      pageOptions: IDocumentsUploadsSummaryPageOptionsDto,
    ) =>
      transformPageOptionsToQueryString(
        'documents-uploads/uploads-summary',
        pageOptions,
      ),
    updateValuesOfEditableMetadataFields: (documentOid: string) =>
      `documents-uploads/editable-metadata-fields-values/${documentOid}`,
    getFileMetadataByIds: (
      projectId: string,
      includeAutomaticallySetFields: boolean = false,
    ) =>
      `documents-uploads/file-metadata/${projectId}?includeAutomaticallySetFields=${includeAutomaticallySetFields}`,
    getDocumentTitle: (documentOid: string) =>
      `documents-uploads/file-metadata/title/${documentOid}`,
  },

  appConfig: {
    getAppConfig: 'app-config',
    setAppConfig: 'app-config',
  },

  checkBot: {
    getCheckBotConfigTemplates: 'checkbot/templates/names',
    getCheckBotConfigFile: (projectId: string, supplierId: string) =>
      `checkbot/config/check-bot-config/${projectId}/${supplierId}`,
    saveCheckBotConfigFile: 'checkbot/config/modify-check-bot-config',
    allChecks: 'checkbot/config/master-template',
    checkCheckBotResults: 'checkbot/check-results',
    checkCheckBotTimeouts: 'checkbot/check-timeouts',
    getTestKeysForTemplates: 'checkbot/config/names-and-test-keys',
    getDigitalizationProjectCheckBotConfigFile: (
      digitalizationProjectId: string,
    ) =>
      `checkbot/config/digitalization-project/check-bot-config/${digitalizationProjectId}`,
    saveDigitalizationProjectCheckBotConfigFile:
      'checkbot/config/digitalization-project/modify-check-bot-config',
    getQueueCount: 'checkbot/queue-count',
    configs: {
      prefix: 'checkbot/configs',
      createConfig(projectId: string, supplierId?: string): string {
        return `${this.prefix}/${projectId}${
          supplierId ? `?supplierId=${supplierId}` : ''
        }`;
      },
      getAssignedConfigs(projectId: string, supplierId?: string): string {
        return `${this.prefix}/assigned/${projectId}${
          supplierId ? `?supplierId=${supplierId}` : ''
        }`;
      },
      getChecks(projectId?: string): string {
        return projectId
          ? `${this.prefix}/checks?projectId=${projectId}`
          : `${this.prefix}/checks`;
      },
      getConfig(configId: string): string {
        return `${this.prefix}/${configId}`;
      },
      getTemplate(templateId: number): string {
        return `${this.prefix}/template/${templateId}`;
      },
      getTemplates(): string {
        return `${this.prefix}/templates`;
      },
      updateConfig(configId: string): string {
        return `${this.prefix}/${configId}`;
      },
    },
  },

  metadataCheck: {
    prefix: 'metadata-check',
    executeMetadataCheck(fileId: string) {
      return `${this.prefix}/execute-metadata-check/${fileId}`;
    },
  },

  documentHistory: {
    getDocumentHistory: (uploadId: string, documentId: string) =>
      `document-history/document/${uploadId}/${documentId}`,
    getDigitalizationDocumentHistory: (uploadId: string, documentId: string) =>
      `document-history/digitalization-document/${uploadId}/${documentId}`,
  },

  digitalizationProjects: {
    addCoworkerToDigitalizationProject: 'digitalization-projects/coworkers',
    addDocuResponsibleToDigitalizationProject: (
      digitalizationProjectId: string,
      docuResponsibleOid: string,
    ) =>
      `digitalization-projects/docu-responsibles/${digitalizationProjectId}/${docuResponsibleOid}`,
    createDigitalizationProject: 'digitalization-projects',
    getDigitalizationProjectCheckBotTemplate: (
      digitalizationProjectId: string,
    ) =>
      `digitalization-projects/check-bot-configuration/${digitalizationProjectId}`,
    getDigitalizationProjectCoworkers: (digitalizationProjectId: string) =>
      `digitalization-projects/coworkers/${digitalizationProjectId}`,
    getDigitalizationProjectDocuResponsibles: (
      digitalizationProjectId: string,
    ) => `digitalization-projects/docu-responsibles/${digitalizationProjectId}`,
    getDigitalizationProjectLocation: (digitalizationProjectId: string) =>
      `digitalization-projects/digitalization-project-data/${digitalizationProjectId}/location`,
    getDigitalizationProjectTitle: (digitalizationProjectId: string) =>
      `digitalization-projects/title/${digitalizationProjectId}`,
    getDigitalizationProjectSettings: (digitalizationProjectId: string) =>
      `digitalization-projects/digitalization-project-data/${digitalizationProjectId}`,
    getDigitalizationProjectsList: (
      pageOptions: IDigitalizationProjectsPageOptionsDto,
    ): string => {
      return transformPageOptionsToQueryString(
        'digitalization-projects',
        pageOptions,
      );
    },
    removeCoworkerFromDigitalizationProject: (
      digitalizationProjectId: string,
      coworkerOid: string,
    ) =>
      `digitalization-projects/coworkers/${digitalizationProjectId}/${coworkerOid}`,
    removeDocuResponsibleFromDigitalizationProject: (
      digitalizationProjectId: string,
      docuResponsibleOid: string,
    ) =>
      `digitalization-projects/docu-responsibles/${digitalizationProjectId}/${docuResponsibleOid}`,
    setCheckBotConfiguration: 'digitalization-projects/check-bot-configuration',
    updateDigitalizationProject: (digitalizationProjectId: string) =>
      `digitalization-projects/digitalization-project-data/${digitalizationProjectId}`,
  },

  digitalizationUploads: {
    getCheckBotDataForDocuments:
      'digitalization-uploads/digitalization-documents-metadata/check-bot-data-for-documents',
    getCheckBotInferenceStatusForMultipleDocuments:
      'digitalization-uploads/digitalization-documents/inference-statuses',
    getDigitalizationDocumentData: (
      documentOid: string,
      includeCheckBotConfig?: boolean,
    ) =>
      `digitalization-uploads/document-data/${documentOid}${includeCheckBotConfig ? '?includeCheckBotConfig=true' : ''}`,
    getDocumentMetadataByIds: (
      digitalizationProjectId: string,
      includeAutomaticallySetFields: boolean = false,
      additionalFieldsToInclude: string[] = [],
    ) => {
      let url = `digitalization-uploads/digitalization-documents-metadata/${digitalizationProjectId}?includeAutomaticallySetFields=${includeAutomaticallySetFields}`;
      if (additionalFieldsToInclude.length) {
        url = `${url}&additionalFieldsToInclude=${additionalFieldsToInclude.join(
          ',',
        )}`;
      }
      return url;
    },
    getDocumentTitle: (documentOid: string) =>
      `digitalization-uploads/document-title/${documentOid}`,
    getPaginatedDigitalizationUploadsSummary: (
      pageOptions: IDigitalizationUploadSummaryPageOptionsDto,
    ) =>
      transformPageOptionsToQueryString(
        'digitalization-uploads/digitalization-uploads-summary',
        pageOptions,
      ),
    getPaginatedDigitalizationDocumentsSummary: (
      pageOptions: IDigitalizationDocumentSummaryPageOptionsDto,
    ) =>
      transformPageOptionsToQueryString(
        'digitalization-uploads/digitalization-documents-summary',
        pageOptions,
      ),
    removeUpload: (uploadId: string) => `digitalization-uploads/${uploadId}`,
    setQualityCheckStatusOfDocument: (
      documentOid: string,
      qualityCheckStatus: QualityCheckStatus,
    ) =>
      `digitalization-uploads/quality-check-status/${documentOid}/${qualityCheckStatus}`,
    setQualityCheckStatusesOfMultipleDocuments: `digitalization-uploads/update-multiple-quality-check-statuses`,
    softDeleteUpload: (uploadId: string, softDelete: boolean) =>
      `digitalization-uploads/soft-delete/${uploadId}/${softDelete}`,
    updateMetadataOfMultipleDocuments: (
      digitalizationProjectId: string,
      acceptMetadataInspection: boolean,
    ) =>
      `digitalization-uploads/digitalization-documents-metadata/${digitalizationProjectId}/${acceptMetadataInspection}`,
    validateReferenceNumber: (
      digitalizationProjectId: string,
      referenceNumber: string,
    ) =>
      `digitalization-uploads/validate/${digitalizationProjectId}/${referenceNumber}`,
  },

  prodisApiIntegration: {
    prefix: 'prodis-api-integration',
    getContentTypeNameById(contentTypeId: string): string {
      return `${this.prefix}/content-types/name/${contentTypeId}`;
    },
    getKksTree(): string {
      return `${this.prefix}/kks-tree`;
    },
    getPreselectedNodes(): string {
      return `${this.prefix}/preselected-nodes`;
    },
    locations(): string {
      return `${this.prefix}/locations`;
    },
    validateKksList(): string {
      return `${this.prefix}/validate-kks-list`;
    },
    resolveDCC(): string {
      return `${this.prefix}/dcc/resolve`;
    },
  },

  commonDocuments: {
    prefix: 'common-documents',
    streamFile(fileId: string, projectType: ProjectType): string {
      return `${this.prefix}/stream-file/${fileId}/${projectType}`;
    },
  },

  patchNotes: {
    prefix: 'patch-notes',
    createPatchNote() {
      return `${this.prefix}/create`;
    },
    getAllPatchNoteVersions() {
      return `${this.prefix}/versions`;
    },
    getCurrentPatchNote() {
      return `${this.prefix}/current`;
    },
    getCurrentVersion() {
      return `${this.prefix}/current-version`;
    },
    getPatchNoteById(patchNoteId: string) {
      return `${this.prefix}/patch-note/${patchNoteId}`;
    },
    updatePatchNoteById(patchNoteId: string) {
      return `${this.prefix}/patch-note/${patchNoteId}`;
    },
  },

  ibl: {
    prefix: 'ibl',
    getIblImportResult(taskId: string): string {
      return `${this.prefix}/import-result/${taskId}`;
    },
    getVgbmAssignmentsForKks(kksId: string): string {
      return `${this.prefix}/vgbm/${kksId}`;
    },
    updateVgbmAssignmentForKks(vgbmAssignmentId: string): string {
      return `${this.prefix}/vgbm/${vgbmAssignmentId}`;
    },
    getVgbkListForProject(projectId: string): string {
      return `${this.prefix}/vgbk-list/${projectId}`;
    },
    getVgbmListForProject(): string {
      return `${this.prefix}/vgbm-list`;
    },
    importIbl(projectId: string, supplierId?: string): string {
      return `${this.prefix}/import/${projectId}${
        supplierId ? `?supplierId=${supplierId}` : ''
      }`;
    },
    resolveKksId(kks: string, location: string): string {
      return `${this.prefix}/resolve-kks-id/${kks}/${location}`;
    },
  },

  auth: {
    prefix: 'oauth',
    isAuthenticated() {
      return `${this.prefix}/is-authenticated`;
    },
  },

  userSettings: {
    prefix: 'user-settings',
    setWebTourSeen(webTour: WebTour): string {
      return `${this.prefix}/web-tour-seen/${webTour}`;
    },
    wasWebTourSeen(webTour: WebTour): string {
      return `${this.prefix}/web-tour-seen/${webTour}`;
    },
  },

  uploadHandler: {
    prefix: 'upload-handler',
    downloadUploadConfirmationFile(projectType: ProjectType, uploadId: string) {
      return `${this.prefix}/upload-confirmation-file/${projectType}/${uploadId}`;
    },
    uploadUpdateHandler: {
      prefix: 'upload-handler/upload-update',
      finalize(uploadCorrelationId: string) {
        return `${this.prefix}/finalize/${uploadCorrelationId}`;
      },
      getUploadUpdateDependencies(uploadId: string) {
        return `${this.prefix}/dependencies/${uploadId}`;
      },
      uploadFile(documentOid: string) {
        return `${this.prefix}/document/${documentOid}`;
      },
    },
    getVersionDiff() {
      return `${this.prefix}/version-diff`;
    },
    isRenditionFileExpected(
      documentId: string,
      projectId: string,
      supplierId?: string,
    ) {
      return `${this.prefix}/rendition-expected/${documentId}/${projectId}${
        supplierId ? `/${supplierId}` : ''
      }`;
    },
    processUpload() {
      return `${this.prefix}/upload`;
    },
    uploadFile() {
      return `${this.prefix}/document`;
    },
  },

  checkBotKks: {
    prefix: 'documents-metadata/check-bot-kks',
    moveCheckBotEstablishedKksCodesToMainKksField(
      documentOid: string,
      validatedOnly: boolean,
    ): string {
      return `${this.prefix}/accept-check-bot-established-kks/${documentOid}?validatedOnly=${validatedOnly}`;
    },
    moveOneCheckBotEstablishedKksToMainKksField(
      documentOid: string,
      kks: string,
    ): string {
      return `${this.prefix}/accept-one-check-bot-established-kks/${documentOid}/${kks}`;
    },
  },

  supplierAuthTokens: {
    prefix: 'supplier-auth-tokens',
    createToken() {
      return `${this.prefix}`;
    },
    getTokensForSupplier(supplierId: string) {
      return `${this.prefix}/${supplierId}`;
    },
    revokeToken(supplierAuthTokenId: string) {
      return `${this.prefix}/${supplierAuthTokenId}`;
    },
  },

  workspaces: {
    prefix: 'workspaces',
    addUserToWorkspaceGroup(groupId: string, userOid: string) {
      return `${this.prefix}/groups/${groupId}/users/${userOid}`;
    },
    addWorkspaceOwner(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/owners`;
    },
    addWorkspaceReviewer(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/reviewers`;
    },
    createWorkspace() {
      return this.prefix;
    },
    createWorkspaceGroup() {
      return `${this.prefix}/groups`;
    },
    validateWorkspaceGroupName(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/groups/validate-name`;
    },
    editWorkspace(workspaceId: string) {
      return `${this.prefix}/${workspaceId}`;
    },
    getWorkspaceById(workspaceId: string) {
      return `${this.prefix}/${workspaceId}`;
    },
    getWorkspaceGroups(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/groups`;
    },
    getWorkspaceGroupNameById(groupId: string) {
      return `${this.prefix}/groups/${groupId}/name`;
    },
    getWorkspaceGroupUsers(groupId: string) {
      return `${this.prefix}/groups/${groupId}/users`;
    },
    getWorkspaceGroupKksRange(groupId: string) {
      return `${this.prefix}/groups/${groupId}/kks-range`;
    },
    getWorkspaceOwners(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/owners`;
    },
    getWorkspaceOwnerCandidates() {
      return `${this.prefix}/owners/candidates`;
    },
    getWorkspaceReviewers(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/reviewers`;
    },
    searchWorkspaceOwnerCandidates() {
      return `${this.prefix}/owners/candidates/search`;
    },
    searchWorkspaceReviewerCandidates() {
      return `${this.prefix}/reviewers/candidates/search`;
    },
    getWorkspaces() {
      return this.prefix;
    },
    removeUserFromWorkspaceGroup(groupId: string, userOid: string) {
      return `${this.prefix}/groups/${groupId}/users/${userOid}`;
    },
    removeWorkspaceGroupKksRange(groupId: string) {
      return `${this.prefix}/groups/${groupId}/kks-range`;
    },
    removeWorkspaceOwner(workspaceId: string, userOid: string) {
      return `${this.prefix}/${workspaceId}/owners/${userOid}`;
    },
    removeWorkspaceReviewer(workspaceId: string, userOid: string) {
      return `${this.prefix}/${workspaceId}/reviewers/${userOid}`;
    },
    upsertWorkspaceGroupKksRange(groupId: string) {
      return `${this.prefix}/groups/${groupId}/kks-range`;
    },
    areKksInWorkspaceGroupKksRange(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/groups/kks-range/validate`;
    },
    canEditWorkspaceDocument(workspaceId: string, documentId: string) {
      return `${this.prefix}/${workspaceId}/groups/${documentId}/edit-access`;
    },
    getKksTreeForWorkspaceCoworker(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/groups/kks-tree`;
    },
    getUsersFromWorkspacesAdGroup() {
      return `${this.prefix}/group-members/search`;
    },
    getExternalUsersFromWorkspacesAdGroup() {
      return `${this.prefix}/group-members/search/true`;
    },
    getDccFulfillment(workspaceId: string) {
      return `${this.prefix}/${workspaceId}/dcc/fulfillment`;
    },
  },
  workspaceUploads: {
    prefix: 'workspace-uploads',
    searchWorkspaceDocuments(workspaceId: string) {
      return `${this.prefix}/documents/search/${workspaceId}`;
    },
    getWorkspaceDocuments(
      workspaceId: string,
      pageOptions: IWorkspaceDocumentsListPageOptionsDto,
    ) {
      return transformPageOptionsToQueryString(
        `${this.prefix}/documents/${workspaceId}`,
        pageOptions,
      );
    },
    getReadOnlyWorkspaceDocuments(
      workspaceId: string,
      pageOptions: IWorkspaceReadOnlyDocumentsListPageOptionsDto,
    ) {
      return transformPageOptionsToQueryString(
        `${this.prefix}/documents/read-only/${workspaceId}`,
        pageOptions,
      );
    },
    uploadDocument(workspaceId: string) {
      return `${this.prefix}/document/${workspaceId}`;
    },
    addReadOnlyDocuments(workspaceId: string) {
      return `${this.prefix}/documents/read-only/${workspaceId}`;
    },
    createWorkingCopies(workspaceId: string) {
      return `${this.prefix}/documents/working-copies/${workspaceId}`;
    },
    getWorkspaceDocumentMetadata(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/metadata`;
    },
    getReadOnlyWorkspaceDocumentMetadata(
      workspaceId: string,
      documentId: string,
    ) {
      return `${this.prefix}/documents/read-only/${workspaceId}/${documentId}/metadata`;
    },
    updateWorkspaceDocumentMetadata(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/metadata`;
    },
    getWorkspaceDocumentFile(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/file`;
    },
    getReadOnlyWorkspaceDocumentFile(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/read-only/${workspaceId}/${documentId}/file`;
    },
    getWorkspaceDocumentRenditionFile(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/rendition-file`;
    },
    getReadOnlyWorkspaceDocumentRenditionFile(
      workspaceId: string,
      documentId: string,
    ) {
      return `${this.prefix}/documents/read-only/${workspaceId}/${documentId}/rendition-file`;
    },
    getWorkspaceDocumentUpdateInfo(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/update-info`;
    },
    setWorkspaceDocumentInReview(workspaceId: string, documentId: string) {
      return (
        `${this.prefix}/documents/${workspaceId}/${documentId}` +
        '/workflow-status/in-review'
      );
    },
    applyWorkspaceDocumentWorkingCopy(workspaceId: string, documentId: string) {
      return (
        `${this.prefix}/documents/${workspaceId}/${documentId}` +
        '/apply-working-copy'
      );
    },
    getReadOnlyWorkspaceDocumentDetailInfo(
      workspaceId: string,
      documentId: string,
    ) {
      return `${this.prefix}/documents/read-only/${workspaceId}/${documentId}/detail-info`;
    },
    updateWorkspaceDocumentFile(workspaceId: string, documentId: string) {
      return `${this.prefix}/documents/${workspaceId}/${documentId}/file`;
    },
  },
};
